/* eslint-disable @typescript-eslint/no-explicit-any */
import {emConfig as  config } from "@/emConfiguration";

import { trimTrailingSlash } from '@emarketer/code-helpers';


const urlPattern = /^https?:\/\//i;
const OrganizationLogoUrl = config.commonLdJsonLogoUrl;
const OrganizationLogo = {
  url: OrganizationLogoUrl,
  contentUrl: OrganizationLogoUrl,
  width: 200,
  height: 200
};
const SocialSharingLogoUrl = config.commonDefaultSocialThumbnailUrl;
const SocialSharingLogo = {
  url: SocialSharingLogoUrl,
  contentUrl: SocialSharingLogoUrl,
  width: 1280,
  height: 720
};
const IsAbsolute = (url: string) => urlPattern.test(url);
const GetGraphItemByType = (items: any[], type: string) => {
  type = type.toLowerCase();
  return items.find(item => {
    const itemType = item['@type'];
    return item['@type']
      && ((typeof (itemType) === 'string' && item['@type'].toLowerCase() === type)
        || (typeof (itemType) !== 'string' && (item['@type'] as string[]).some(t => t.toLowerCase() === type)));
  });
};

const EnforceAbsoluteUrl = (url: string | undefined) => {
  if (!url) return "";

  if (IsAbsolute(url)) {
    return url;
  }

  if (!url.startsWith('/')) {
    url = `/${url}`;
  }

  return `${config.iiHomeUrl}${url}`;
};

const CleanupOrganization = (graphItems: any[]) => {
  const org = GetGraphItemByType(graphItems, 'organization');
  if (!org) return;

  // TODO: figure out how to tell if real primary image was set.
  if (org.logo) {
    org.logo = {
      ...org.logo,
      ...OrganizationLogo
    };
  }
};

const CleanupWebPage = (isFrontPage: boolean, graphItems: any[]) => {
  const webpage = GetGraphItemByType(graphItems, 'webpage');
  // Guard against missing webpage
  if (!webpage) return;
  
  // Typically use the page title, but our home page is something long and for it we just want II.
  if (isFrontPage) {
    webpage.name = 'EMARKETER';
  }
};

const CleanupImageObject = (graphItems: any[], featuredImage: string | undefined) => {
  const imageObject = GetGraphItemByType(graphItems, 'imageobject');

  if (!imageObject) return;

  // If a featured image was set, we want to use that. Othewrise use the default sharing one.
  if (featuredImage) {
    imageObject.url = trimTrailingSlash( EnforceAbsoluteUrl(featuredImage));
    imageObject.contentUrl = imageObject.url;
  }
  else {
    // Copy over all of the props.
    Object.entries(SocialSharingLogo).forEach(([key, value]) => {
      imageObject[key] = value;
    });
  }
};

const CleanupPotentialActions = (actions: any[]) => {
  if (!actions || !Array.isArray(actions)) return [];
  
  return actions
    .filter(action => action?.['@type']?.toLowerCase() !== 'searchaction')
    .map(action => {
      if (!action) return action;
      
      // Handle target as array or string (objects like EntryPoint are filtered out with SearchAction)
      let processedTarget = action.target;
      if (Array.isArray(action.target)) {
        processedTarget = action.target.map(EnforceAbsoluteUrl);
      } else if (typeof action.target === 'string') {
        processedTarget = EnforceAbsoluteUrl(action.target);
      }
      
      return {
        ...action,
        target: processedTarget
      };
    });
};



export const AddMissingLastItemUrlForBreadCrumb = (graphItems: any[]): void => {
  const breadcrumblist = GetGraphItemByType(graphItems, 'breadcrumblist');

  if (!breadcrumblist || !breadcrumblist.itemListElement || !breadcrumblist["@id"]) return;

  // Guard against empty itemListElement array
  if (breadcrumblist.itemListElement.length === 0) return;

  const lastBreadcrumb = breadcrumblist.itemListElement.slice(-1)[0];
  
  // Guard against undefined lastBreadcrumb
  if (!lastBreadcrumb) return;

  // If the last breadcrumb does not have an item property or it is empty, add the item property
  if (!lastBreadcrumb.item) {
    const pageUrl = breadcrumblist["@id"].replace('#breadcrumb', '');
    lastBreadcrumb.item = pageUrl;
  }

};
/**
 * Utility to clean up the incoming json-ld schema that is auto-generated by Yoast.
 * 
 * Ensures all incoming URLs are absolute, that our images are consistent, certain
 * "page actions" are removed, etc.
 */
export const CleanupRawSchema = (isFrontPage: boolean, 
  schema: string | undefined, 
  featuredImage: string | undefined): any => {
  if (!schema) { return schema; }

  try {
    const parsed = JSON.parse(schema);
    let graph = parsed['@graph'] as any[];

    // Guard against missing or invalid @graph
    if (!graph || !Array.isArray(graph)) {
      console.error('[CleanupRawSchema] Schema missing @graph array:', schema.substring(0, 200));
      return schema;
    }

    // Do a map on each item. There are some properties that we can safely always remap if they're set.
    //  Some are more specific.
    graph = graph.map(graphEntry => {
      if (!graphEntry) return graphEntry;

      if (graphEntry.url) {
        graphEntry.url = EnforceAbsoluteUrl(graphEntry.url);
      }

      // If there's a potential action array, we want to remove the search action. 
      // Our site isn't really wired up to support it so it's misleading on Google.
      if (graphEntry.potentialAction) {
        graphEntry.potentialAction = CleanupPotentialActions(graphEntry.potentialAction);
      }
      return graphEntry;
    });

    CleanupOrganization(graph);
    CleanupWebPage(isFrontPage, graph);
    CleanupImageObject(graph, featuredImage);
    AddMissingLastItemUrlForBreadCrumb(graph);
    parsed["@graph"] = graph;

    return JSON.stringify(parsed);
  } catch (error) {
    // Log error with context for debugging in Netlify logs
    console.error('[CleanupRawSchema] Error processing schema:', error);
    console.error('[CleanupRawSchema] Schema preview:', schema?.substring(0, 500));
    return schema;
  }
};

